import { TESTIMONIAL_AVATARS as AV } from "./testimonialAvatars";

export const HERO_WORDS = ["hospitals", "clinics", "physio centers", "diagnostic labs"];

/** Hero headline — cycles the last word only */
export const HERO_AUDIENCE_WORDS = ["Hospital", "Clinic", "Physiotherapist"];

/** Four product lines — home, /products, footer. */
export const PRODUCT_PILLARS = [
  {
    id: "hms",
    icon: "🏥",
    title: "AI HMS",
    desc: "Hospital management — OPD, IPD, pharmacy, billing, and admin desks on one record.",
    link: "/products",
    hash: "products-modules",
  },
  {
    id: "emr",
    icon: "🩺",
    title: "AI EMR",
    desc: "ABDM-ready case sheets, e-Rx, and specialty workflows for every consult.",
    link: "/products",
    hash: "products-modules",
  },
  {
    id: "physio",
    icon: "💗",
    title: "Physiotherapist",
    desc: "V1–V10 protocols, outcomes, team assignment, and progress billing — flagship rehab desk.",
    link: "/for/physiotherapy",
    featured: true,
  },
  {
    id: "agents",
    icon: "✨",
    title: "AI Agents",
    desc: "Discharge, nutrition, SOAP, voice receptionist, and more — drafts inside your chart.",
    link: "/products",
    hash: "agents",
    homeHash: "agents",
  },
];

/** Footer Products column — physio first after the three pillars. */
export const FOOTER_PRODUCT_LINKS = [
  { label: "AI Hospital EMR", path: "/products" },
  { label: "AI Agents", path: "/products", hash: "agents" },
  { label: "Physiotherapy", path: "/for/physiotherapy", highlight: true },
  { label: "Nutritional Agent", path: "/nutritional-agent" },
  { label: "Orthopedic EMR", path: "/for/orthopedics" },
  { label: "Cardiology EMR", path: "/for/cardiology" },
  { label: "Pulmonology EMR", path: "/for/pulmonology" },
  { label: "Oncology EMR", path: "/for/oncology" },
  { label: "Integrations", path: "/", hash: "integrations" },
  { label: "Pricing", path: "/pricing" },
];

export const MODULES = [
  { icon: "🩺", title: "EMR", desc: "Structured, ABDM-ready electronic medical records for every visit and specialty.", accent: true, badge: "Core" },
  { icon: "🧑‍⚕️", title: "OPD & Token Queue", desc: "Digital token queue, visit intake, and doctor workflow in one screen." },
  { icon: "🛏️", title: "IPD & Case Sheets", desc: "Admission to discharge case sheets, orders, and nursing notes." },
  { icon: "🧴", title: "Pharmacy", desc: "Inventory, dispensing, and prescriptions linked directly to the EMR." },
  { icon: "🧾", title: "Billing & GST", desc: "Package billing, partial payments, and GST-ready reporting on one dashboard." },
  {
    icon: "💗",
    title: "Physiotherapy",
    desc: "Protocol tracking, outcomes, team assignment, and session billing built for physio-led clinics.",
    link: "/for/physiotherapy",
    featured: true,
  },
  {
    icon: "🥗",
    title: "Nutrition AI",
    desc: "AI-generated diet plans from an agentic pipeline, reviewed by your clinicians.",
    accent: true,
    badge: "AI",
    link: "/nutritional-agent",
  },
  { icon: "📝", title: "SOAP Agent", desc: "Structured SOAP notes in 22 languages — draft for clinician review in the chart.", accent: true, badge: "AI" },
  { icon: "🩻", title: "Radiology", desc: "Imaging orders, queue, and reports linked to the same patient chart — no re-entry between OPD and radiology." },
  {
    icon: "💊",
    title: "e-Prescription",
    desc: "One-click generate prescription from the consult — send to the patient on WhatsApp, trackable and paper-free.",
    accent: true,
    badge: "Core",
  },
  {
    icon: "📋",
    title: "AI Patient Summary",
    desc: "Draft IPD discharge summaries from case sheets, orders, and vitals — clinician reviews before sign.",
    accent: true,
    badge: "AI",
    link: "/generate-ai-patient-summary",
  },
  {
    icon: "🔗",
    title: "Integrations",
    desc: "Google Drive patient sync, WhatsApp e-Rx and follow-up reminders, Google Meet telemedicine, Calendar, and Docs — linked to every chart.",
    accent: true,
    badge: "Connect",
  },
];

export const PAIN_POINTS = [
  { title: "Records scattered across", detail: "paper files, WhatsApp, and three different apps that don't talk to each other." },
  { title: "Billing that doesn't match", detail: "what the doctor actually did — partial packages and add-ons tracked by memory." },
  { title: "No time to build", detail: "a digital presence, so patients can't find or trust the clinic online." },
];

export const FIXES = [
  {
    title: "One record, every department",
    desc: "EMR, OPD, IPD, pharmacy, and billing share the same patient record — nothing re-typed twice.",
  },
  {
    title: "Compliant by default",
    desc: "ABDM and HIPAA-aligned data handling built into the platform, not bolted on later.",
  },
  {
    title: "AI that does the busywork",
    desc: "SOAP notes, nutrition plans, and clinical drafting handled by AI agents your team reviews and approves.",
  },
];

export const DIFFERENTIATORS = [
  {
    title: "Customised to your practice",
    desc: "OPD queues, physio protocols, nutrition desks, and billing rules — mapped to how you work, with ongoing tweaks as you grow.",
  },
  {
    title: "Not a rigid EMR template",
    desc: "Unlike off-the-shelf software, Deepaarogya adapts to ortho, ENT, physio-led, and multi-specialty setups without forcing new habits.",
  },
  {
    title: "24×7 deployment support",
    desc: "New clinic or migrating from an older system — our engineers guide setup, training, and go-live so your team is never stuck.",
  },
  {
    title: "AI agents inside the chart",
    desc: "Discharge, nutrition, summary, and SOAP agents sit in your workflow — drafts for clinician review, not black-box automation.",
  },
];

export const STEPS = [
  { n: "01", title: "Digitize your workflow", desc: "We migrate your existing patient records and map your clinic's exact OPD/IPD flow — usually within days, not months." },
  { n: "02", title: "Your team starts using it", desc: "Front desk, doctors, and pharmacy log in with role-based access scoped to what each person needs." },
  { n: "03", title: "AI takes on the busywork", desc: "SOAP notes, nutrition planning, and reporting start running in the background as your team gets comfortable." },
];

export const SPECIALTIES = [
  { name: "Orthopedics", status: "live" },
  { name: "Pediatrics", status: "live" },
  { name: "Physiotherapy", status: "live" },
  { name: "ENT", status: "live" },
  { name: "General Medicine", status: "live" },
  { name: "Nutrition & Dietetics", status: "live" },
  { name: "Cardiology", status: "live" },
  { name: "Pulmonology", status: "live" },
  { name: "Oncology", status: "live" },
  { name: "Multi-specialty", status: "live" },
  { name: "Dentistry", status: "soon" },
  { name: "Dermatology", status: "soon" },
  { name: "Ophthalmology", status: "soon" },
];

/** Footer EMR specialties — ordered for market + product strength */
export const EMR_SPECIALTIES = [
  "Orthopedics",
  "Pediatrics",
  "Physiotherapy",
  "ENT",
  "General Medicine",
  "Nutrition & Dietetics",
  "Dentistry",
  "Dermatology",
  "Ophthalmology",
  "Cardiology",
  "Pulmonology",
  "Oncology",
];

/** Footer specialty links — descriptive internal URLs for SEO/AEO. */
export const EMR_SPECIALTY_LINKS = [
  { name: "Orthopedics", path: "/for/orthopedics" },
  { name: "Pediatrics", path: "/products" },
  { name: "Physiotherapy", path: "/for/physiotherapy" },
  { name: "ENT", path: "/products" },
  { name: "General Medicine", path: "/products" },
  { name: "Nutrition & Dietetics", path: "/nutritional-agent" },
  { name: "Dentistry", path: "/products" },
  { name: "Dermatology", path: "/products" },
  { name: "Ophthalmology", path: "/products" },
  { name: "Cardiology", path: "/for/cardiology" },
  { name: "Pulmonology", path: "/for/pulmonology" },
  { name: "Oncology", path: "/for/oncology" },
];

export const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/deepaarogya",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/people/Deepaarogya-AI/61575079247029/#",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/deepaarogyaai/",
  },
];

export const FAQS = [
  {
    question: "How much time will it take to install?",
    answer:
      "Most clinics go live in a few days, not months. We map your OPD/IPD flow, migrate essential patient data, set up roles, and train your team. Timeline depends on clinic size — a solo or small clinic is usually quicker; multi-specialty hospitals take a short guided rollout with our deployment engineers.",
  },
  {
    question: "Is my patient data secure?",
    answer:
      "Yes. Deepaarogya is built with hospital-grade controls: encryption in transit and at rest, role-based access, and audit trails. We are ABDM-ready (ABHA-linked records and consent-based sharing) and HIPAA-aligned. Your patient data stays in your workflows — it is not used to train a shared public model.",
  },
  {
    question: "Can you design it as per our specialty workflow?",
    answer:
      "Yes. The platform is highly customisable. We work with your team to configure specialty workflows — ortho, physio, nutrition, ENT, and multi-specialty desks — so the system matches how you already practice. Connect with our team and we help make it working for your clinic.",
  },
  {
    question: "Can an individual clinic buy it?",
    answer:
      "Absolutely yes. Deepaarogya is designed for individual clinics and multi-specialty hospitals. Based on the plan you choose (Clinic Lite, Solo Doctor, Clinic Mid, or Multi-Specialty), you get the modules and seats you need — and you can add AI packs or specialties as you grow.",
  },
  {
    question: "What is Deepaarogya AI?",
    answer:
      "Deepaarogya AI is an AI hospital and clinic operating system (EMR software) for India — OPD, IPD, pharmacy, billing, physiotherapy, nutrition, and clinical AI agents on one ABDM-ready patient record for doctors and clinics.",
  },
  {
    question: "What AI agents are included?",
    answer:
      "Eight agents sit inside the chart: Voice Receptionist, Discharge Summary, Nutritional, WhatsApp Follow-up (e-Rx and reminders), Visit Summary, Doc, Deep Healthcare, and SOAP (22-language support). Every output is a draft for clinician review — not autonomous diagnosis.",
  },
  {
    question: "Can I store patient data in my own Google Drive?",
    answer:
      "Yes. Deepaarogya syncs clinical files to your hospital's Google Drive — X-rays, photos, lab PDFs, and reports stay in your account while the EMR links each file to the correct patient and visit.",
  },
  {
    question: "Do you support WhatsApp e-prescriptions and follow-up reminders?",
    answer:
      "Yes. Send signed e-prescriptions on WhatsApp from the consult chart, plus automated appointment reminders, medicine follow-ups, and revisit nudges — with delivery status and audit trail. WhatsApp messaging is an optional add-on pack.",
  },
  {
    question: "Do you support WhatsApp, Google Drive, and telemedicine?",
    answer:
      "Yes. Sync patient files to your Google Drive, send e-prescriptions and follow-up reminders on WhatsApp, and run online consults on Google Meet — all linked to the same patient chart.",
  },
];

export const TESTIMONIALS = [
  {
    name: "Dr. Atchyut",
    role: "Orthopaedic Surgeon · Founder",
    hospital: "Apple Ortho · Complete Joint Care, Kurnool",
    text:
      "We moved from our older system to Deepaarogya AI — OPD, physiotherapy, and nutrition now flow on one chart. The platform was customised to how Apple Ortho runs, on desktop and mobile.",
    img: AV.drAtchyut,
    initials: "DA",
  },
  {
    name: "Dr. Priya N.",
    role: "ENT Specialist",
    hospital: "New ENT Clinic",
    text:
      "I just started my new clinic and Deepaarogya helped me with 24-hour support and guidance. Telemedicine for online presence, simpler one-click e-prescriptions — that is what makes the platform work for a solo practice.",
    img: AV.female1,
    initials: "PN",
  },
  {
    name: "Dr. Sanjay M.",
    role: "Consultant Physician",
    hospital: "Aster DM Healthcare",
    text:
      "We were looking for a discharge summary AI agent. Deepaarogya's discharge agent is the best — in 2–3 minutes we get an approved discharge summary ready for clinician sign-off.",
    img: AV.male2,
    initials: "SM",
  },
  {
    name: "Dr. Kavitha R.",
    role: "General Physician",
    hospital: "Urban Care Clinic, Hyderabad",
    text:
      "Unlike rigid EMRs, the team customised workflows to match our OPD. Nutrition and physio desks connect without duplicate entry — exactly what a growing clinic needs.",
    img: AV.female2,
    initials: "KR",
  },
  {
    name: "Dr. Arun V.",
    role: "Physiotherapist",
    hospital: "Motion Rehab Centre, Bengaluru",
    text:
      "Session tracking, billing, and doctor notes stay on one chart. Mobile-friendly for therapists on the floor — and support responds fast when we need changes.",
    img: AV.male4,
    initials: "AV",
  },
  {
    name: "Dr. Meera S.",
    role: "Dietitian",
    hospital: "NutriCare Desk, Pune",
    text:
      "Nutrition plans link to vitals and diagnosis from the EMR. I review AI drafts instead of starting from blank templates — saves hours every week.",
    img: AV.female3,
    initials: "MS",
  },
];

export const WHATSAPP_DEMO_URL =
  "https://wa.me/918979523908?text=I'm%20interested%20in%20your%20AI%20Hospital%20OS!";
export const CALENDAR_URL = "https://calendar.app.google/vBej1yvKF2qr8u3U7";
export const CONTACT_EMAIL = "contact@deepaarogya.com";

export const JOURNEY_PHASES = [
  { n: "01", title: "Engage", desc: "WhatsApp, online booking, or clinic walk-in." },
  { n: "02", title: "Intake", desc: "Demographics, vitals, habits, body composition." },
  { n: "03", title: "Consult", desc: "EMR case sheet, SOAP AI, specialty desks." },
  { n: "04", title: "Coordinate", desc: "Lab, Radiology, Physiotherapy, Nutrition, Pharmacy." },
  { n: "05", title: "Bill & follow-up", desc: "GST billing, reminders, next visit." },
];

/** Touchpoints shown on the patient journey hub */
export const JOURNEY_TOUCHPOINTS = [
  "Past Visits",
  "Lab",
  "Radiology",
  "Dietitian",
  "Medicine",
  "Physiotherapy",
  "Billing",
  "IPD",
  "Telemedicine",
];

/** Demo patient chart — Rajesh strip below queue showcase */
export const PATIENT_CHART_DEMO = {
  name: "Rajesh K.",
  uhid: "UHID-28471",
  visit: "Orthopaedic follow-up · Physiotherapy + Billing on one chart",
};

export const JOURNEY_DEPARTMENTS = [
  "Front Desk",
  "OPD",
  "Lab",
  "IPD",
  "Physiotherapy",
  "Nutrition",
  "Pharmacy",
  "Billing",
  "Follow-up",
];

/** OPD queue mock — marketing showcase (reference: ref_patient_journey.html) */
export const JOURNEY_QUEUE_DEMO = {
  url: "app.deepaarogya.com/opd/queue",
  active: [
    {
      time: "11:22 AM",
      token: "4",
      name: "Sree Teja G",
      age: "31 yrs",
      uhid: "H50-002265",
      visit: 1,
      diagnosis: "Hand pain",
      journey: ["#ef4444", "#22c55e", "#22c55e", "#ef4444", "#eab308"],
      status: "In Consultation",
      statusClass: "consult",
      focus: true,
    },
    {
      time: "12:26 PM",
      token: "12",
      name: "Moukthika Saranam",
      age: "16 yrs",
      uhid: "H50-002275",
      visit: 1,
      diagnosis: "Grade 3 ankle sprain",
      journey: ["#ef4444", "#22c55e", "#e5e3ea", "#e5e3ea", "#eab308"],
      status: "Arrived",
      statusClass: "arrived",
    },
    {
      time: "—",
      token: "—",
      tokenMuted: true,
      name: "Jashwa Reddy D",
      age: "19 yrs",
      uhid: "H50-002268",
      visit: 1,
      diagnosis: "Knee osteoarthritis",
      journey: null,
      status: "Scheduled",
      statusClass: "sched",
    },
    {
      time: "—",
      token: "—",
      tokenMuted: true,
      name: "Malathi H",
      age: "32 yrs",
      uhid: "H50-002274",
      visit: 1,
      diagnosis: "Hand pain",
      journey: null,
      status: "Scheduled",
      statusClass: "sched",
    },
  ],
  completed: [
    {
      time: "10:30 AM",
      token: "1",
      name: "Devesh Reddy D",
      age: "19 yrs",
      uhid: "H50-002261",
      visit: 1,
      diagnosis: "Synovitis of hindfoot with periostitis",
      status: "Completed",
      statusClass: "done",
    },
  ],
  caseSheet: {
    uhid: "H50-002265",
    name: "Ms Sree Teja G",
    meta: "(31y, F) · Kurnool",
    tabs: [
      { label: "Dx", active: true },
      { label: "Vitals", dot: "red" },
      { label: "Clinical" },
      { label: "Investigation", dot: "green" },
      { label: "OOR", dot: "red" },
      { label: "Treatment" },
      { label: "Complaint", dot: "green" },
      { label: "Exam" },
      { label: "Physiotherapy" },
      { label: "Medicine" },
      { label: "Follow-up", dot: "green" },
      { label: "Body", dot: "green" },
    ],
    tooltip: "Sree Teja G (31 yrs) — open case sheet",
  },
};

export const ACCESS_FEATURES = [
  {
    title: "Operate from anywhere",
    desc: "Full clinic workflow on desktop, tablet, and mobile — front desk, ward rounds, or follow-ups on the go.",
    icons: ["mobile", "desktop"],
  },
  {
    title: "Role-based access (RBAC)",
    desc: "Doctor, nurse, reception, pharmacy, and admin — each role sees only what they need. No shared logins.",
    icons: ["rbac"],
  },
  {
    title: "HIPAA-aligned & encrypted",
    desc: "AES-256 in transit and at rest. Audit trails on chart actions. BAA available for enterprise.",
    icons: ["shield"],
  },
];

export const INFRA_PARTNERS = [
  { name: "AWS", tag: "Cloud infrastructure" },
  { name: "NVIDIA", tag: "AI compute" },
  { name: "IIT Madras", tag: "Incubation" },
  { name: "IIT Roorkee", tag: "Research" },
  { name: "ABDM", tag: "Health stack ready" },
];

export const INTEGRATIONS = [
  {
    name: "Google Drive patient sync",
    logo: "google-drive",
    icon: "📁",
    accent: true,
    featured: true,
    wide: true,
    desc: "Store clinical files in your own Google Drive — X-rays, surgery photos, lab PDFs, and reports sync to the patient chart. Your hospital keeps ownership; Deepaarogya links every file to the right visit.",
  },
  {
    name: "WhatsApp e-Prescription",
    logo: "whatsapp",
    icon: "💬",
    accent: true,
    featured: true,
    wide: true,
    desc: "One-click e-prescription from the consult — send a clear, trackable Rx to the patient on WhatsApp after clinician sign-off. No paper printouts, full audit trail on the chart.",
  },
  {
    name: "WhatsApp follow-up & reminders",
    logo: "whatsapp",
    icon: "🔔",
    accent: true,
    featured: true,
    agent: true,
    desc: "Appointment reminders, medicine follow-ups, and revisit nudges on WhatsApp — automated from the same EMR chart with delivery status and staff review.",
  },
  {
    name: "Google Meet",
    logo: "google-meet",
    icon: "📹",
    accent: true,
    desc: "Online patient telemedicine — start a video consult from the appointment and keep notes on the same EMR record.",
  },
  {
    name: "Google Calendar",
    logo: "google-calendar",
    icon: "📅",
    desc: "Sync OPD slots and follow-ups so clinic bookings stay aligned with your team calendar.",
  },
  {
    name: "Google Docs",
    logo: "google-docs",
    icon: "📄",
    desc: "Draft discharge notes, handouts, and patient instructions without leaving Deepaarogya.",
  },
  {
    name: "Integration support",
    logo: "api",
    icon: "🔗",
    accent: true,
    desc: "Discharge Summary API, partner webhooks, and hospital connectors — our team helps with setup and go-live.",
  },
];
